import React from "react";
import { ApolloError } from "@apollo/client";
import { getListQueryStatus, NodeID, QueryStatus } from "@hex-insights/app-modules";
import { ChildrenProps, DefaultContextValue, omitKeys, useRequiredContext } from "@hex-insights/core";
import {
	AcademicTerm,
	LearningObjective,
	LearningObjectiveCategory,
	ReportCardSectionLearningObjectivesNavigationListQuery,
	ReportCardSectionLearningObjectivesNavigationListQueryVariables,
	useModelFromID,
	useReportCardSectionLearningObjectivesNavigationListQuery,
} from "../../../../../Utilities";
import {
	LearningObjectiveCategoryFormat,
	LearningObjectiveFormat,
	ReportCardSectionPolicyFormat,
} from "../../../../Format";

// TODO unite reused logic with that in submission page

export type ReportCardSectionAspectsNavigationSection = {
	id: NodeID | string;
	name: string;
	aspectCategories: {
		id: NodeID | string;
		name: string;
		aspects: {
			id: NodeID | string;
			name: string;
		}[];
	}[];
};

export type ReportCardSectionAspectsContextValue = {
	isLoading: boolean;
	data: ReportCardSectionAspectsNavigationSection[] | undefined;
	error: ApolloError | undefined;
	queryStatus: QueryStatus;
};

const ReportCardSectionAspectsContext = React.createContext<ReportCardSectionAspectsContextValue>(
	new DefaultContextValue("ReportCardSectionAspectsContext") as any,
);

export function useReportCardSectionAspectsContext() {
	return useRequiredContext(ReportCardSectionAspectsContext, "useReportCardSectionAspectsContext");
}

export type ReportCardSectionAspectsContextManagerProps = {
	academicTermID: AcademicTerm["id"];
	sectionID: NodeID;
} & Partial<ChildrenProps>;

export function ReportCardSectionAspectsContextManager({
	academicTermID,
	sectionID,
	children,
}: ReportCardSectionAspectsContextManagerProps) {
	const { queryVariables, skip } = useLearningObjectivesVariables(academicTermID, sectionID);

	const { loading, data, error } = useReportCardSectionLearningObjectivesNavigationListQuery({
		variables: queryVariables,
		skip,
	});

	const contextValue = React.useMemo(() => {
		const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "reportCardSectionPolicyConnection" });

		if (!data) {
			return {
				isLoading: loading,
				data: undefined,
				error,
				queryStatus,
			};
		}

		// start reuse
		const learningObjectiveCategoryMap: Record<
			LearningObjectiveCategory["id"],
			ReportCardSectionLearningObjectivesNavigationListQuery["learningObjectiveCategoryConnection"]["edges"][0]["node"]
		> = {};
		const learningObjectivesMap: Record<
			LearningObjective["id"],
			Pick<LearningObjective, "id"> & {
				learningObjectiveCategory: Pick<LearningObjectiveCategory, "id">;
			}
		> = {};
		for (let i = 0; i < data.learningObjectiveCategoryConnection.edges.length; i++) {
			const category = data.learningObjectiveCategoryConnection.edges[i].node;
			learningObjectiveCategoryMap[category.id] = category;
			for (let j = 0; j < category.learningObjectives.length; j++) {
				const objective = category.learningObjectives[j];
				learningObjectivesMap[objective.id] = {
					...objective,
					learningObjectiveCategory: omitKeys(category, ["learningObjectives"]),
				};
			}
		}
		// end reuse

		const formattedData: ReportCardSectionAspectsNavigationSection[] = [];
		for (let i = 0; i < data.reportCardSectionPolicyConnection.edges.length; i++) {
			const policy = data.reportCardSectionPolicyConnection.edges[i].node;
			const formattedSection: ReportCardSectionAspectsNavigationSection = {
				id: policy.id,
				name: ReportCardSectionPolicyFormat.name(policy),
				aspectCategories: [],
			};

			if (policy.usesOverallMark && policy.isOverallMarkManual) {
				formattedSection.aspectCategories.push({
					id: "overallMark",
					name: ReportCardSectionPolicyFormat.Fields.overallMarkLabel(policy.overallMarkLabel),
					aspects: [
						{
							id: "overallMark",
							name: ReportCardSectionPolicyFormat.Fields.overallMarkLabel(policy.overallMarkLabel),
						},
					],
				});
			}

			if (policy.usesGeneralComments) {
				formattedSection.aspectCategories.push({
					id: "generalComments",
					name: "Comments",
					aspects: [
						{
							id: "generalComment1",
							name: "Interests and Achievements",
						},
						{
							id: "generalComment2",
							name: "Characteristics Of Effective Learning",
						},
					],
				});
			}

			// reused logic
			const seenLearningObjectiveCategoryIDs = new Set<string>();
			for (let j = 0; j < policy.reportCardSections.length; j++) {
				const section = policy.reportCardSections[j];
				for (let k = 0; k < section.reportCardSectionLearningObjectives.length; k++) {
					const rcslo = section.reportCardSectionLearningObjectives[k];
					const lo = learningObjectivesMap[rcslo.learningObjectiveID];
					if (!lo) {
						continue;
					}
					const locID = lo.learningObjectiveCategory.id;
					if (seenLearningObjectiveCategoryIDs.has(locID)) {
						continue;
					}

					seenLearningObjectiveCategoryIDs.add(locID);
					const category = learningObjectiveCategoryMap[locID];
					if (!category || category.learningObjectives.length === 0) {
						continue;
					}

					formattedSection.aspectCategories.push({
						id: category.id,
						name: LearningObjectiveCategoryFormat.name(category),
						aspects: category.learningObjectives.map((lo) => ({
							id: lo.id,
							name: LearningObjectiveFormat.name(lo),
						})),
					});
				}
			}

			if (policy.usesComment) {
				formattedSection.aspectCategories.push({
					id: "comment",
					name: "Comments",
					aspects: [
						{
							id: "comment",
							name: ReportCardSectionPolicyFormat.Fields.commentLabel(policy.commentLabel),
						},
					],
				});
			}

			formattedData.push(formattedSection);
		}

		return {
			isLoading: loading,
			data: formattedData,
			error,
			queryStatus,
		};
	}, [loading, data, error]);

	return (
		<ReportCardSectionAspectsContext.Provider value={contextValue}>{children}</ReportCardSectionAspectsContext.Provider>
	);
}

function useLearningObjectivesVariables(academicTermID: AcademicTerm["id"], sectionID: NodeID) {
	const { modelName } = useModelFromID(sectionID);

	return React.useMemo<{
		queryVariables: ReportCardSectionLearningObjectivesNavigationListQueryVariables;
		sectionModelName: string | undefined;
		skip?: boolean;
	}>(() => {
		switch (modelName) {
			case "CourseSection":
				return {
					queryVariables: {
						academicTermID,
						courseSectionID: sectionID,
					},
					sectionModelName: modelName,
				};
			case "HomeRoomSection":
				return {
					queryVariables: {
						academicTermID,
						homeRoomSectionID: sectionID,
					},
					sectionModelName: modelName,
				};
			default:
				return {
					queryVariables: {
						academicTermID,
					},
					sectionModelName: modelName,
					skip: true,
				};
		}
	}, [academicTermID, sectionID, modelName]);
}
